import { FileSearch } from 'lucide-react'
import { SiteLayout } from '../components/ui/site-chrome'
import { TrustPage } from '../components/ui/trust-page'
import { ParallaxScrollSection } from '../components/ui/parallax-scroll-feature-section'

const caseStudies = [
  {
    eyebrow: 'Platform Diagnosis',
    title: 'PopMart-style e-commerce under load',
    description:
      'A high-demand brand was losing revenue through slow performance and poor conversion flow. We mapped the architecture, surfaced the bottlenecks, and sequenced the highest-leverage fixes in a single diagnostic sprint.',
    imageUrl:
      'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80&fit=crop',
    metrics: [
      { label: 'Page load reduced', value: '2.4×' },
      { label: 'Conversion lift', value: '+18%' },
    ],
    reverse: false,
  },
  {
    eyebrow: 'Founder Support',
    title: 'Non-technical founder, series A crunch',
    description:
      'With a vendor relationship going sideways and a board presentation looming, the founder needed a translator: someone to scope the work, challenge timelines, and protect product-market learning.',
    imageUrl:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80&fit=crop',
    metrics: [
      { label: 'Weeks to clarity', value: '3' },
      { label: 'Vendor risks caught', value: '7' },
    ],
    reverse: true,
  },
  {
    eyebrow: 'Enterprise AI',
    title: 'AI from pilot to production',
    description:
      'Most AI projects die between prototype and production. We built the connective tissue: data pipelines, permissions, monitoring, governance, and measurable business outcomes that stuck.',
    imageUrl:
      'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=800&q=80&fit=crop',
    metrics: [
      { label: 'Workflows shipped', value: '12' },
      { label: 'Ops hours saved / mo', value: '340' },
    ],
    reverse: false,
  },
]

export default function CaseStudiesPage() {
  return (
    <SiteLayout
      current="/case-studies"
      title="Case Studies & Use Cases | Zadro"
      description="Use cases for Zadro, including PopMart-style platform diagnosis, AI adoption, fractional CTO leadership, and product innovation pilots."
    >
      <TrustPage
        Icon={FileSearch}
        visual="cases"
        eyebrow="Case Studies"
        heroTitle="Use cases that show how the circuit gets fixed."
        heroCopy="These pages help buyers see the shape of the work: platform diagnosis, AI adoption, founder coaching, product innovation, workshops, and scale-ready technical leadership."
        proof={[
          'Focus on translating technical symptoms into business impact.',
          'Use cases are written for decision-makers, not only engineers.',
          'Each case points toward a diagnostic call with a concrete problem.',
        ]}
        cards={[
          {
            title: 'PopMart-style platform diagnosis',
            copy: 'A high-demand brand can still lose revenue through slow performance, weak architecture, poor conversion flow, or hidden platform drag.',
          },
          {
            title: 'Non-technical founder support',
            copy: 'Founders need a translator who can help them scope, hire, manage vendors, challenge timelines, and protect product-market learning.',
          },
          {
            title: 'Enterprise AI done correctly',
            copy: 'Companies need AI workflows that connect to data, permissions, monitoring, governance, and measurable outcomes.',
          },
        ]}
        sections={[
          {
            kicker: 'Pattern',
            title: 'Every case starts with a measurable leak.',
            copy: 'A vague problem becomes useful when it can be mapped to lost time, delayed delivery, wasted spend, risk, churn, conversion, or missed product opportunity.',
            items: [
              'Performance bottlenecks that reduce conversion or trust',
              'Roadmaps that create activity but not business movement',
              'AI pilots that never reach production',
              'Teams that need ownership, review, and decision rhythm',
            ],
          },
          {
            kicker: 'What Buyers See',
            title: 'Proof does not always mean public client logos.',
            copy: 'For strategic work, trust can be built through clear thinking, use-case depth, transparent methodology, and the ability to explain tradeoffs in business terms.',
            items: [
              'Diagnosis before prescription',
              'Business metric tied to technical constraint',
              'Clear sequence of highest-leverage fixes',
              'Readable executive output that internal teams can act on',
            ],
          },
        ]}
        ctaTitle="Ready to see your case mapped out?"
        ctaMarqueeItems={[
          'Platform Diagnosis',
          'Founder Coaching',
          'AI to Production',
          'Team Leadership',
          'Product Clarity',
        ]}
        beforeCta={
          <ParallaxScrollSection
            eyebrow="In Depth"
            heading="Three cases. Three different circuits fixed."
            cases={caseStudies}
          />
        }
      />
    </SiteLayout>
  )
}
